import {
    Component,
    Input,
    OnInit,
    OnChanges,
    OnDestroy,
    ElementRef,
    HostBinding,
    Renderer2,
    RendererStyleFlags2,
    ChangeDetectionStrategy,
} from '@angular/core';
import { Router } from '@angular/router';
import { concatLatestFrom } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Observable, BehaviorSubject, of, combineLatest, Subject } from 'rxjs';
import {

    switchMap, distinctUntilChanged, filter, map, takeUntil, take, tap, shareReplay, first,
} from 'rxjs/operators';

import { pieceProductCarouselAnimations } from './piece-product-carousel.animations';
import {
    BorderRadiusType,
    PieceProductCarouselSource,
    PieceProductCarouselViewStyle,
    PRODUCT_CAROUSEL_DEFAULT_NUMBER_COLUMNS,
    PRODUCT_CAROUSEL_DEFAULT_NUMBER_MAX_BULLET_PAGES,
    PRODUCT_CAROUSEL_DEFAULT_NUMBER_ROWS,
    PRODUCT_IMAGE_CLASS,
    ProductCarousel,
} from './piece-product-carousel.model';

import { ProductsHelpers } from '@api/catalog/products/products.helpers';
import { Product } from '@api/catalog/products/products.models';
import { MoneyHelpers } from '@api/util/money.helpers';
import { BrowserOnly } from '@core/data-providers/platform/platform.decorators';
import { Resolutions } from '@core/domain/state/core.state';
import { EnvironmentCmsUrls } from '@core/domain/types/environment-config.types';
import { getCurrencyInfo } from '@core/infrastructure/state/core.selectors';
import { ConfigService } from '@core/services/config/config.service';
import { PlatformService } from '@core/services/platform/platform.service';
import { TypeOfPager, TypePositionOfArrow } from '@features/cms-render/domain/models/common-settings-styles.models';
import { ContentItemTree } from '@features/cms-render/domain/models/contentItemTree';
import { LazyImage } from '@features/cms-render/domain/models/lazyImage';
import { Link } from '@features/cms-render/domain/models/link';
import {
    CommonStylesRenderDirective,
} from '@features/cms-render/ui/directives/common-styles-render/common-styles-render.directive';
import { AssetsSharedService } from '@features/cms-render/ui/services/assets-shared/assets-shared.service';
import { ProductsArrayFeaturePublicApi } from '@features/productsArray';
import { isDefined } from '@frontend/common/utils';

@Component({
    selector: 'piece-product-carousel',
    templateUrl: './piece-product-carousel.component.html',
    styleUrls: ['./piece-product-carousel.component.scss'],
    animations: [ pieceProductCarouselAnimations ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})

export class PieceProductCarouselComponent extends CommonStylesRenderDirective implements OnInit, OnChanges, OnDestroy {
    @Input() public element: ContentItemTree;

    @Input() public direction: string;

    public displayComponentArrows: boolean = false;

    public displayComponentPager: boolean = false;

    private sourceType: PieceProductCarouselSource = 'category';

    private readonly sourceType$: BehaviorSubject<PieceProductCarouselSource> = new BehaviorSubject(this.sourceType);

    private readonly sourceId$: BehaviorSubject<string | null> = new BehaviorSubject(null);

    private readonly _imageOffset$: BehaviorSubject<number> = new BehaviorSubject(0);

    public imageOffset$: Observable<number> = this._imageOffset$.asObservable().pipe(
        tap((imageOffset) => { this.imageOffset = imageOffset; })
    );

    public imageOffset: number;

    public products$!: Observable<Product[] | ProductCarousel[] | undefined>;

    public productPage$!: Observable<Product[] | ProductCarousel[] | undefined>;

    public currency$!: Observable<string | null>;

    public currentSlide: number = 0;

    public imageClass = PRODUCT_IMAGE_CLASS;

    private readonly breakpointChange$: BehaviorSubject<boolean> = new BehaviorSubject(true);

    private readonly clearSubscriptions$: Subject<void> = new Subject();

    private imageBorderRadiusType!: BorderRadiusType;

    private imageBorderRadius!: string;

    private scrollMode!: 'free' | 'pager';

    @HostBinding('class.scroll-free')
    public isScrollFree!: boolean;

    @HostBinding('class.scroll-pager')
    public isScrollPager!: boolean;

    private viewStyle!: PieceProductCarouselViewStyle;

    @HostBinding('class.view-style-grid')
    public isViewStyleGrid!: boolean;

    @HostBinding('class.view-style-silhouette')
    public isViewStyleSilhouette!: boolean;

    @HostBinding('class.view-style-double')
    public isViewStyleDouble!: boolean;

    private pagerStyle!: TypeOfPager;

    public arrowPosition!: TypePositionOfArrow;

    @HostBinding('class.pager-style-dot')
    public isPagerStyleDot!: boolean;

    @HostBinding('class.pager-style-box')
    public isPagerStyleBox!: boolean;

    @HostBinding('class.pager-style-solid')
    public isPagerStyleSolid!: boolean;

    @HostBinding('class.pager-style-dashed')
    public isPagerStyleDashed!: boolean;

    @HostBinding('class.dark-theme')
    public isDarkTheme!: boolean;

    @HostBinding('class.light-theme')
    public isLightTheme!: boolean;

    public maxBulletPages!: number;

    public isVisibleName!: boolean;

    public isVisibleTitle!: boolean;

    public isVisiblePrice!: boolean;

    public isVisibleOldPrice!: boolean;

    public isVisibleCart!: boolean;

    public isVisibleButtonLink!: boolean;

    public showSliderHeader!: boolean;

    public _isFirstSlide!: boolean;

    public _isLastSlide!: boolean;

    private _numberOfProducts!: number;

    public _currentPage!: number;

    public _totalPages!: number;

    public _totalPagesArr!: any[];

    public _slidesPerPage!: number;

    private maxProducts: number;

    public isBrowser: boolean = this.platform.isBrowser();

    public currencyInfo$ = this.store.select(getCurrencyInfo).pipe(first());

    constructor(
        private readonly elementRef: ElementRef,
        private readonly assetsSharedService: AssetsSharedService,
        private readonly renderer: Renderer2,
        private readonly platform: PlatformService,
        private readonly configService: ConfigService,
        private readonly productsArrayPublicApi: ProductsArrayFeaturePublicApi,
        private readonly store: Store,
        private readonly router: Router,
    ) {
        super();
    }

    public ngOnInit(): void {
        super.setDefaultHostStyles();
        this.setConfig();
        this.setStyles();
        this.onMediaChange();

        this.maxProducts = this.element?.settings?.general.maxProducts
            ? Number(this.element.settings.general.maxProducts)
            : undefined;

        const productsFromCategory$ = this.sourceId$.pipe(
            filter(isDefined),
            distinctUntilChanged(),
            switchMap((categoryId: string) => this.productsArrayPublicApi.getCategoryPlatformStudio$(categoryId)),
            switchMap((products) => this.productsArrayPublicApi.getProductsArrayWithoutCategory$( // TODO ECOMDUTI-20989 Recuperar los productos de la STORE en vez de la API en caso de tenerlos ya cargados
                products?.productIds?.slice(0, this.maxProducts).toString()
            )),
        );

        const productsFromBundle$ = this.sourceId$.pipe(
            filter(isDefined),
            distinctUntilChanged(),
            switchMap((productId: string) => {
                return this.productsArrayPublicApi.getProductsArrayWithoutCategory$(productId).pipe( // TODO ECOMDUTI-20989 Recuperar los productos de la STORE en vez de la API en caso de tenerlos ya cargados
                    map((product) => {
                        return isDefined(product[0]) ? product[0].bundleProducts.slice(0, this.maxProducts) : [];
                    })
                );
            })
        );

        const productsFromList$ = this.sourceId$.pipe(
            filter(isDefined),
            distinctUntilChanged(),
            map((sourceId: string) => this.sanitizeProducts(sourceId)),
            switchMap((productIds: string) => {
                return this.productsArrayPublicApi.getProductsArrayWithoutCategory$(productIds).pipe( // TODO ECOMDUTI-20989 Recuperar los productos de la STORE en vez de la API en caso de tenerlos ya cargados
                    map((products) => products.slice(0, this.maxProducts))
                );
            })
        );

        this.products$ = this.sourceType$.pipe(
            filter(isDefined),
            distinctUntilChanged((prev: PieceProductCarouselSource, next: PieceProductCarouselSource) => prev === next),
            switchMap((sourceType) => {
                this.sourceType = sourceType;

                if (sourceType === 'category') {
                    return productsFromCategory$;
                }

                if (sourceType === 'productBundle') {
                    return productsFromBundle$;
                }

                if (sourceType === 'productList') {
                    return productsFromList$;
                }

                return of([] as Product[]);
            }),
            concatLatestFrom(() => [ this.imageOffset$.pipe(take(1)), this.currencyInfo$]),
            map(([products = [], imageOffset, curr]) => {
                const _products = products.map((product) => {
                    let priceWithCurrencySymbol = MoneyHelpers.formatPriceWithCurrencySymbol(
                        product.priceInfo.price,
                        curr
                    );

                    return {
                        ...product,
                        link: this.getProductLink(product),
                        productImageData: this.getProductImageData(product),
                        priceInfo: {
                            price: product.priceInfo.price,
                            priceWithCurrencySymbol: priceWithCurrencySymbol,
                        },
                    };
                });

                this._numberOfProducts = _products.length;
                this.updateArrows(imageOffset);
                this.updateCounters();

                return _products;
            }),
            shareReplay(1),
            takeUntil(this.clearSubscriptions$)
        );

        this.productPage$ = combineLatest([this.products$, this._imageOffset$, this.breakpointChange$]).pipe(
            map(([products = [], imageOffset]) => {
                if (!this.platform.isBrowser()) {
                    return products;
                }
                return this.isScrollFree ? products : products.slice(imageOffset, imageOffset + this._slidesPerPage);
            }),
            takeUntil(this.clearSubscriptions$)
        );
    }

    public ngOnChanges(): void {
        this.setStyles();
        this.onMediaChange();
    }

    private getProductImageData(product: Product): LazyImage | undefined {
        const width = 600;
        const media = ProductsHelpers.firstImageForFirstColor(product.colors, 'category_page', width);

        return this.assetsSharedService.productImageData(
            media,
            width,
            `${this.configService.getConfigUrl<EnvironmentCmsUrls>('cmsUrls').content}/assets/`,
            this.element?.settings?.general?.impolicy
        );
    }

    private gridSize(): { numberOfColumns: number; numberOfRows: number } {
        if (this.element?.settings?.general) {
            const gridSize = {
                numberOfColumns: +(this.element.settings.general.numberOfColumns || PRODUCT_CAROUSEL_DEFAULT_NUMBER_COLUMNS),
                numberOfRows: this.element.settings.general.numberOfRows || PRODUCT_CAROUSEL_DEFAULT_NUMBER_ROWS,
            };
            if (this.resolution === Resolutions.MOBILE) {
                if (this.element.settings.general.numberOfColumnsMobile) {
                    gridSize.numberOfColumns = this.element.settings.general.numberOfColumnsMobile;
                }
                if (this.element.settings.general.numberOfRowsMobile) {
                    gridSize.numberOfRows = this.element.settings.general.numberOfRowsMobile;
                }
            }
            return gridSize;
        }

        return {
            numberOfColumns: PRODUCT_CAROUSEL_DEFAULT_NUMBER_COLUMNS,
            numberOfRows: PRODUCT_CAROUSEL_DEFAULT_NUMBER_ROWS,
        };
    }

    private getProductLink(product: Product | ProductCarousel): Link {
        const link: Link = {
            type: 'product',
            pieceName: this.element?.name,
            productId: product.id,
        };

        if ('categoryId' in product) {
            link.categoryId = product.categoryId;
        }

        return link;
    }

    private onMediaChange(): void {
        this.updateCounters();
        this.updateArrows(this.imageOffset ?? 0);

        const element = this.elementRef.nativeElement;
        const gridSize = this.gridSize();
        this.renderer.setStyle(element, '--grid-columns', gridSize.numberOfColumns, RendererStyleFlags2.DashCase);
        this.renderer.setStyle(element, '--grid-rows', gridSize.numberOfRows, RendererStyleFlags2.DashCase);
        this.breakpointChange$.next(true);
    }

    @BrowserOnly<PieceProductCarouselComponent, undefined>(undefined)
    public goToSlide(slide: number, imageOffset: number) {
        const diffPages = slide - this.currentSlide;
        const _imageOffset = Math.max(imageOffset + this._slidesPerPage * diffPages, 0);
        this._imageOffset$.next(_imageOffset);
        this.updateArrows(_imageOffset);
        this.currentSlide = slide;
    }

    @BrowserOnly<PieceProductCarouselComponent, undefined>(undefined)
    public prevSlide(imageOffset: number): void {
        const _imageOffset = Math.max(imageOffset - this._slidesPerPage, 0);
        this._imageOffset$.next(_imageOffset);
        this.updateArrows(_imageOffset);
        this.currentSlide--;
    }

    @BrowserOnly<PieceProductCarouselComponent, undefined>(undefined)
    public nextSlide(imageOffset: number): void {
        const _imageOffset = imageOffset + this._slidesPerPage;
        this._imageOffset$.next(_imageOffset);
        this.updateArrows(_imageOffset);
        this.currentSlide++;
    }

    private slidesPerPage(): number {
        const gridSize = this.gridSize();
        return gridSize.numberOfColumns * gridSize.numberOfRows;
    }

    private isFirstSlide(imageOffset: number) {
        return imageOffset === 0;
    }

    private isLastSlide(imageOffset: number) {
        if (!isDefined(this._numberOfProducts)) {
            return true;
        }

        return imageOffset >= this._numberOfProducts - this._slidesPerPage;
    }

    private currentPage(imageOffset: number) {
        return Math.round(imageOffset / this._slidesPerPage) + 1;
    }

    private totalPages() {
        if (!isDefined(this._numberOfProducts)) {
            return 0;
        }

        return Math.ceil(this._numberOfProducts / this._slidesPerPage);
    }

    public trackByIndex(index: number): number {
        return index;
    }

    public trackByProduct(index: number, item: Product): string {
        return item.id;
    }

    private sanitizeProducts(productsIds: string): string {
        return productsIds.replace(/\s/g, '');
    }

    private setConfig(): void {
        if (isDefined(this.element?.settings?.general)) {
            this.viewStyle = this.element?.settings?.general?.viewStyle ?? 'grid-image';
            this.isViewStyleGrid = this.viewStyle === 'grid-image';
            this.isViewStyleSilhouette = this.viewStyle === 'silhouette-image';
            this.isViewStyleDouble = this.viewStyle === 'double-image';

            this.pagerStyle = this.element?.settings?.general?.pagerStyle ?? 'dot';
            this.arrowPosition = this.element?.settings?.general?.arrowPosition ?? 'inside-left-right';
            this.isPagerStyleDot = this.pagerStyle === 'dot';
            this.isPagerStyleBox = this.pagerStyle === 'box';
            this.isPagerStyleSolid = this.pagerStyle === 'solid';
            this.isPagerStyleDashed = this.pagerStyle === 'dashed';
            this.isDarkTheme = this.element?.settings?.general?.pagerStyleTheme === 'dark-theme';
            this.isLightTheme = this.element?.settings?.general?.pagerStyleTheme === 'light-theme';
            this.maxBulletPages = this.element
                ? Number(this.element.settings?.general.maxBulletPages)
                : PRODUCT_CAROUSEL_DEFAULT_NUMBER_MAX_BULLET_PAGES;

            this.isVisibleName = this.element?.settings?.general?.displayProductNames;
            this.isVisibleTitle = this.element?.settings?.general?.showTitle;
            this.isVisiblePrice = this.element?.settings?.general?.displayProductPrices;
            this.isVisibleOldPrice = this.isVisiblePrice && this.element.settings.general.displayOldProductPrices;
            this.isVisibleButtonLink = !!this.element?.settings?.general?.displayButtonLink;
        }
    }

    private setStyles(): void {
        if (isDefined(this.element?.settings?.general)) {
            const settings = this.element.settings.general;
            const styles = this.element.settings.styles;
            const element = this.elementRef.nativeElement;

            if (isDefined(settings.sourceType)) {
                this.sourceType$.next(settings.sourceType);
            }

            if (isDefined(settings.sourceId)) {
                this.sourceId$.next(settings.sourceId);
            }

            this.scrollMode = settings.scrollMode ?? 'free';
            this.isScrollFree = this.scrollMode === 'free';
            this.isScrollPager = this.scrollMode === 'pager';

            if (this.isScrollPager) {
                this.renderer.setStyle(element, '--grid-gaps', styles.spaceBetweenProducts || '1rem', RendererStyleFlags2.DashCase);
                this.renderer.setStyle(element, '--grid-justify-content', styles.justifyContent || 'flex-start', RendererStyleFlags2.DashCase);
                this.renderer.setStyle(element, '--grid-initial-offset', styles.initialOffset || '0', RendererStyleFlags2.DashCase);
            } else if (this.isScrollFree) {
                this.renderer.setStyle(element, 'justify-content', styles.justifyContent || 'flex-start', RendererStyleFlags2.DashCase);
                this.renderer.setStyle(element, '--gaps', styles.spaceBetweenProducts || '1rem', RendererStyleFlags2.DashCase);
            }

            this.imageBorderRadiusType = styles.imageBorderRadiusType ?? 'straight';
            this.imageBorderRadius = styles.imageBorderRadius ?? '0pt';

            this.imageClass = `${PRODUCT_IMAGE_CLASS} ${this.imageBorderRadiusType}`;
            this.renderer.setStyle(element, '--image-border-radius', this.imageBorderRadius, RendererStyleFlags2.DashCase);

            this.displayComponentArrows = settings.displayComponentArrows;
            this.displayComponentPager = settings.displayComponentPager;

            const { objectFit, objectPosition } = styles;

            if (!!objectFit) {
                this.renderer.setStyle(element, '--imageObjectFit', `${objectFit}`, 2);
            }

            if (!!objectPosition) {
                this.renderer.setStyle(element, '--imageObjectPosition', `${objectPosition}`, 2);
            }
        }
    }

    // Ocultamos el header si el numero de productos es mas pequeño o igual al numero a mostrar en una pagina
    private updateHeader(): void {
        if (this._numberOfProducts > this._slidesPerPage || this.imageOffset > 0) {
            this.showSliderHeader = this.element.settings.general.showTitle
            && this.element.settings.general.textGridTitle
            && this.element.settings.general.textGridTitle.length > 0
            || this.element.settings.general.arrowPosition === 'header-right';
        } else {
            this.showSliderHeader = this.element.settings.general.showTitle
            && this.element.settings.general.textGridTitle
            && this.element.settings.general.textGridTitle.length > 0;
        }
    }

    private updateArrows(imageOffset: number): void {
        this._isFirstSlide = this.isFirstSlide(imageOffset);
        this._isLastSlide = this.isLastSlide(imageOffset);
        this._currentPage = this.currentPage(imageOffset);
        this.updateHeader();
    }

    private updateCounters(): void {
        this._slidesPerPage = this.slidesPerPage();
        this._totalPages = this.totalPages();
        this._totalPagesArr = [].constructor(this._totalPages);
    }

    public openLink(productLocationPath: string): void {
        this.router.navigateByUrl(productLocationPath);
    }

    public ngOnDestroy() {
        this.clearSubscriptions$.next();
        this.clearSubscriptions$.complete();
    }
}
